import React, { useEffect, useState } from "react";
import useAdminReservation from "../../market/warehouse-baserow/useAdminReservation";
import { getClassById } from "../../market/warehouse-baserow/useAdminClasses";
import { useConfig } from "../../utensils/ConfigContext";
import "./SnackUserReservation.css";
import { FaTrash } from "react-icons/fa";
import { useUser } from "../../utensils/UserContext"; // 👈 Asegúrate de tener esto
import useMail from "../../market/warehouse-baserow/useMail";

const SnackUserReservations = ({ shouldRefresh, forceOpenTab }) => {
  const { reservations, loading, error, refetch, deleteReservation } =
    useAdminReservation();
  const { apiToken } = useConfig();
  const [deletingId, setDeletingId] = useState(null);
  const [classData, setClassData] = useState({});
  const { user } = useUser(); // 👈 Faltaba esta línea
  const { notifyClassCancellation } = useMail();

  // ===== Helpers para restricción de cancelación (≥ 1h antes) =====
  // Mapa de día en español -> índice JS (0=Domingo ... 6=Sábado)
  const DAY_INDEX = {
    Domingo: 0,
    Lunes: 1,
    Martes: 2,
    Miércoles: 3,
    Miercoles: 3, // por si llega sin tilde
    Jueves: 4,
    Viernes: 5,
    Sábado: 6,
    Sabado: 6, // por si llega sin tilde
  };

  // Parsear "Lunes: 09:00 - 10:00 (60 min)" -> { dayName, start: "09:00", end: "10:00" }
  const parseReservaLabel = (label) => {
    if (typeof label !== "string") return null;
    const parts = label.split(":");
    if (parts.length < 2) return null;
    const dayName = parts[0].trim();
    const m = parts[1].match(/(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/);
    if (!m) return null;
    return { dayName, start: m[1], end: m[2] };
  };

  // Fecha/hora del PRÓXIMO inicio de la franja (hoy si aún no empezó, si ya pasó: la próxima semana)
  const getNextStartDate = (dayName, startHHmm) => {
    const targetDow = DAY_INDEX[dayName];
    if (targetDow == null) return null;

    const now = new Date(); // zona local del navegador (tu app está en Colombia)
    const currentDow = now.getDay();

    const [sh, sm] = startHHmm.split(":").map(Number);
    const candidate = new Date(now);
    candidate.setHours(sh, sm, 0, 0);

    let deltaDays = (targetDow - currentDow + 7) % 7;
    // Si es hoy y la hora de inicio ya pasó, saltar a la próxima semana
    if (deltaDays === 0 && candidate <= now) {
      deltaDays = 7;
    }
    // Ajustar al día objetivo
    const result = new Date(candidate);
    result.setDate(now.getDate() + deltaDays);
    return result;
  };

  // ¿Se puede eliminar? Solo si falta al menos 1 hora para el inicio
  const canDeleteReservation = (reservaLabel) => {
    const parsed = parseReservaLabel(reservaLabel);
    if (!parsed) return false; // si no se puede interpretar, no permitir cancelar
    const { dayName, start } = parsed;
    const startDate = getNextStartDate(dayName, start);
    if (!startDate) return false;

    const now = new Date();
    const oneHourMs = 60 * 60 * 1000;
    return now.getTime() <= startDate.getTime() - oneHourMs;
  };

  // 👇 Actualizar reservas si se activa la bandera externa
  useEffect(() => {
    if (shouldRefresh) {
      refetch(user.Email); // 👈 usar email dinámico
    }
  }, [shouldRefresh, refetch]);

  // Obtener la info de clase para cada reserva
  useEffect(() => {
    const fetchAllClasses = async () => {
      const newMap = {};
      for (const res of reservations) {
        if (res.Clase && !classData[res.Clase]) {
          try {
            const cls = await getClassById(res.Clase, apiToken);
            newMap[res.Clase] = cls;
          } catch (error) {
            console.warn("No se pudo cargar clase:", res.Clase);
          }
        }
      }
      setClassData((prev) => ({ ...prev, ...newMap }));
    };

    if (reservations.length > 0) {
      fetchAllClasses();
    }
  }, [reservations, apiToken]);

  const handleDelete = async (id, reservaLabel) => {
    // Validar ventana de cancelación (≥ 1h antes)
    if (!canDeleteReservation(reservaLabel)) {
      alert(
        "Solo puedes cancelar hasta 1 hora antes del inicio de tu reserva."
      );
      return;
    }

    const confirm = window.confirm("¿Estás seguro de eliminar esta reserva?");
    if (!confirm) return;

    try {
      setDeletingId(id);
      await deleteReservation(id);
      await refetch(user.Email);

      // ✅ Emula clic o reabre el acordeón "Mis reservas"
      if (typeof forceOpenTab === "function") {
        forceOpenTab();
      }
    } catch (err) {
      console.error("Error eliminando:", err.message);
      alert("Ocurrió un error al intentar eliminar.");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <p>Cargando reservas...</p>;
  if (error) return <p>Error: {error}</p>;

  // ✅ Función auxiliar para contar reservas activas por clase
  const countReservationsByClassAndDay = (classId, diaTexto) =>
    reservations.filter(
      (r) => r.Clase === classId && r.Estado && r.Reserva?.startsWith(diaTexto)
    ).length;

  return (
    <div className="training-plan-container">
      <h2 className="mb-4">Reservas registradas</h2>

      <div className="workouts-cards-container">
        {reservations.map((res) => {
          const cls = classData[res.Clase];
          const dia = res.Reserva?.split(":")[0]; // "Lunes"
          const total = countReservationsByClassAndDay(res.Clase, dia);

          // Bloquear botón si ya no está en ventana de cancelación
          const deletable = canDeleteReservation(res.Reserva);

          return (
            <div key={res.id} className="card shadow-sm p-3">
              <h5 className="text-primary">
                {cls?.Nombre || "Clase desconocida"}
              </h5>
              <p>
                <strong>Detalle:</strong> {res.Detalle}
              </p>

              <p>
                <strong>Horario reservado:</strong> {res.Reserva}
              </p>

              {cls ? (
                <>
                  <p>
                    <strong>Instructor:</strong> {cls.Instructor}
                  </p>

                  <p>
                    <strong>Cupo:</strong> {cls.Cupo}
                  </p>
                  <p>
                    <strong>Zona:</strong>{" "}
                    <span className="badge bg-secondary">{cls.Zona}</span>
                  </p>

                  <p>
                    <strong>Reservas actuales:</strong> {total}
                  </p>
                </>
              ) : (
                <p className="text-danger">
                  Información de clase no disponible.
                </p>
              )}

              {res["Creado en"] ? (
                <p className="text-muted" style={{ fontSize: "0.85rem" }}>
                  <strong>Creada el:</strong>{" "}
                  {new Date(res["Creado en"]).toLocaleString("es-CO", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              ) : null}

              <div className="mt-3 d-flex justify-content-end">
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleDelete(res.id, res.Reserva)}
                  disabled={deletingId === res.id || !deletable}
                  title={
                    !deletable
                      ? "Solo puedes cancelar hasta 1 hora antes del inicio."
                      : undefined
                  }
                >
                  <FaTrash className="me-1" /> Eliminar
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SnackUserReservations;
